import React, { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '../hooks/useClickOutside';

export function FilterPopover({ label, value, onChange, options, includeAllOption = true, allLabel = 'All', className = '' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const close = useCallback(() => setOpen(false), []);
    useClickOutside(ref, close);

    const selectedOption = options.find((o) => o.value === value);
    const isActive = value !== '' && value !== undefined;

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') setOpen(false);
    };

    return (
        <div className={`relative ${className}`} ref={ref} onKeyDown={handleKeyDown}>
            {isActive ? (
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors bg-accent-50 dark:bg-accent-900/20 border-accent-200 dark:border-accent-700 text-accent-700 dark:text-accent-300"
                >
                    <span>{label}: {selectedOption?.label || value}</span>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSelect(''); }}
                        className="text-accent-400 hover:text-accent-600 dark:hover:text-accent-200 ml-0.5"
                        aria-label={`Clear ${label} filter`}
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                    <span>{label}</span>
                    <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            )}

            {open && (
                <div className="absolute left-0 top-full mt-1 z-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg min-w-[160px] py-1 max-h-64 overflow-auto">
                    {includeAllOption && (
                        <button
                            type="button"
                            onClick={() => handleSelect('')}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                        >
                            <span className="w-4 text-center text-accent-500">{!isActive ? '\u2713' : ''}</span>
                            <span>{allLabel}</span>
                        </button>
                    )}
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelect(opt.value)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                        >
                            <span className="w-4 text-center text-accent-500">{value === opt.value ? '\u2713' : ''}</span>
                            <span>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
